import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  PhoneFilled,
  PauseOutlined,
  CaretRightOutlined,
  SwapOutlined,
  UsergroupAddOutlined,
  AppstoreOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  MinusOutlined,
  CoffeeOutlined,
  UserOutlined,
  HolderOutlined,
  ArrowLeftOutlined,
  PoweroffOutlined,
  CheckCircleFilled,
  AudioOutlined,
  AudioMutedOutlined,
  DeleteOutlined,
  ApiOutlined,
  DisconnectOutlined,
  WarningOutlined,
  HistoryOutlined,
  ArrowUpOutlined,
  StopOutlined,
  LoadingOutlined,
  CloseCircleFilled,
  SearchOutlined,
  DownOutlined,
} from "@ant-design/icons";
import { message } from "antd";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  addParticipant,
  cancelTransfer,
  completeTransfer,
  createSoftphoneSession,
  getAgentPresence,
  getCallbackList,
  getConferenceTargets,
  getCallHistory,
  getCallHistoryPage,
  getCallStatus,
  getSoftphoneIceServers,
  getTelecallerStats,
  hangupCall,
  holdCall,
  initiateCall,
  removeParticipant,
  resumeCall,
  saveCallDisposition,
  setAgentPresence,
  transferCall,
  updateParticipant,
} from "../../assets/api/dialerService";
import { loadSip } from "../../assets/softphone/loadSip";
import Softphone, { PhoneState } from "../../assets/softphone/softphone";
import "./CallWidget.scss";

/*
 * Floating, draggable call widget - the telecaller's phone.
 * The launcher can be dragged anywhere on the page; a tap (no drag) opens a
 * phone-sized panel holding every calling option. Everything it shows comes
 * from the dialer / telecaller APIs; Recents is GET /dialer/calls/history/.
 *
 * Flow (WebRTC flow doc): Connect Phone -> Django issues a SIP credential ->
 * SIP.js registers the browser -> mark Available -> Call (Django) -> the
 * engine rings this browser -> Answer -> customer is dialled -> Hang up
 * (Django) -> disposition.
 */

// The engine rings this browser before dialling the customer. Answering that
// ring automatically makes Call feel like dialling out (one click, as in the
// Call Engine's softphone.js). The flow doc's default is an explicit Answer
// click - set false to bring the Answer / Reject buttons back.
const AUTO_ANSWER = true;

const PHONE_LABELS = {
  [PhoneState.OFFLINE]: { label: "Phone offline", tone: "muted" },
  [PhoneState.CONNECTING]: { label: "Connecting…", tone: "amber" },
  [PhoneState.FAILED]: { label: "Phone error", tone: "muted" },
};
const GOING_LIVE_LABEL = { label: "Going live…", tone: "amber" };

const FAB_SIZE = 56;
const PANEL_W = 350;
const PANEL_H = 660;
const EDGE = 12;
const DRAG_THRESHOLD = 5;
const STORAGE_KEY = "call_widget_fab_pos";
const MAX_NUMBER = 20;
const DIAL_CHAR = /^[0-9*#+]$/;
const cleanNumber = (v) => v.replace(/[^\d+*#]/g, "");

const KEYS = [
  ["1", ""], ["2", "ABC"], ["3", "DEF"],
  ["4", "GHI"], ["5", "JKL"], ["6", "MNO"],
  ["7", "PQRS"], ["8", "TUV"], ["9", "WXYZ"],
  ["*", ""], ["0", "+"], ["#", ""],
];

// Keys are the backend's presence values. Only available/break can be set;
// the rest are reported by the Call Engine. ringing and wrap_up are not in
// the docs: ringing while the engine rings the agent leg, and wrap_up for
// ~20s after each call before the engine returns the agent to available.
const AGENT_STATUSES = [
  { key: "available", label: "Ready", tone: "green", icon: <CheckCircleFilled />, settable: true },
  { key: "break", label: "On Break", tone: "amber", icon: <CoffeeOutlined />, settable: true },
  { key: "ringing", label: "Ringing", tone: "blue", icon: <PhoneFilled /> },
  { key: "on_call", label: "On Call", tone: "blue", icon: <PhoneFilled /> },
  { key: "wrap_up", label: "Wrap-up", tone: "blue", icon: <ClockCircleOutlined /> },
  { key: "offline", label: "Offline", tone: "muted", icon: <PoweroffOutlined /> },
];
const SETTABLE_STATUSES = AGENT_STATUSES.filter((s) => s.settable);
const ON_CALL_STATUS = AGENT_STATUSES.find((s) => s.key === "on_call");
const LIVE_PRESENCE = ["on_call", "ringing"];
// Presence that needs no "going live": ready, or busy with a call.
const READY_PRESENCE = ["available", "on_call", "ringing", "wrap_up"];

// Shown while the widget connects the phone and makes the agent Available.
const GO_LIVE_STEPS = [
  { key: "mic", label: "Checking your microphone", hint: "If your browser asks, click Allow." },
  { key: "line", label: "Connecting your phone line", hint: "Setting up a secure line to the call server." },
  { key: "live", label: "Making you available", hint: "Letting the system know you're ready for calls." },
];
// While the engine has the agent in wrap-up, re-check until it lets go.
const WRAP_UP_POLL_MS = 5000;

const STATUS_HINTS = {
  break: "You're on break.",
  on_call: "You are already on a call.",
  ringing: "A call is ringing your phone.",
  wrap_up: "Finishing your last call. You'll be Ready in a few seconds.",
  offline: "Your phone is connected but you're not available yet.",
};
// Statuses the agent can leave by going Ready from the keypad.
const NOT_READY_PRESENCE = ["break", "offline"];
const NOT_CONFIGURED_HINT = "Your phone is not configured. Please contact the administrator.";

// Call Engine states after which the call is over. Seen on the dev engine:
// originating, bridged, held and - once ended - "hangup". The rest are
// guesses kept as a safety net.
const ENDED_STATES = [
  "completed", "ended", "hangup", "hungup", "terminated",
  "failed", "busy", "no_answer", "cancelled", "canceled", "rejected",
];
const STATUS_POLL_MS = 2000;
// While someone added to the call is on it, re-read who is free every few
// seconds: a participant who reads as available again has left the call.
const TARGETS_POLL_MS = 5000;
// Right after Add the engine is still dialling them and they can still read
// as available, so a fresh participant is never dropped on that alone.
const PARTICIPANT_GRACE_MS = 10000;
// After SIP.js registers, how long to wait for the engine to agree (10 x 2s).
const REGISTER_CONFIRM_TRIES = 10;
const REGISTER_CONFIRM_MS = 2000;
// How long to wait for SIP.js itself to register before giving up.
const REGISTER_TIMEOUT_MS = 20000;
// Idle check that the engine still sees this phone as registered.
const REGISTRATION_CHECK_MS = 30000;

const TRANSFER_MODES = [
  { key: "attended", label: "Attended", hint: "The number is called first while the customer waits. You can then complete or cancel the transfer." },
  { key: "blind", label: "Blind", hint: "The customer is connected straight away and you leave the call." },
];

const PARTICIPANT_MODES = [
  { key: "listen", label: "Listen" },
  { key: "speak", label: "Speak" },
  { key: "whisper", label: "Whisper" },
];

// value is what PATCH /dialer/calls/<id>/disposition/ receives - only the
// outcomes listed in the dialer API docs. Replace with the business-approved
// list (or an API for it) once one exists.
const DISPOSITIONS = [
  { label: "Promise To Pay", value: "ptp" },
  { label: "Paid", value: "paid" },
  { label: "Call Back", value: "callback" },
  { label: "No Answer", value: "no_answer" },
  { label: "Wrong Number", value: "wrong_number" },
  { label: "Not Interested", value: "not_interested" },
];
const dispositionLabel = (value) =>
  DISPOSITIONS.find((d) => d.value === value)?.label || value;

// Call history (GET /dialer/calls/history/): labels from the history API doc.
const CALL_STATE_LABELS = {
  originating: "Calling",
  ringing: "Ringing",
  answered: "Answered",
  bridged: "Connected",
  held: "On hold",
  hangup: "Completed",
  failed: "Failed",
};
const HISTORY_FILTERS = [
  { key: "", label: "All" },
  { key: "hangup", label: "Completed" },
  { key: "failed", label: "Failed" },
];
const HISTORY_PAGE_SIZE = 20;
const HISTORY_SKELETON_ROWS = 6; // first page
const HISTORY_MORE_SKELETON_ROWS = 2; // under the list while loading more

// Placeholder rows shaped like a Recents row while history is fetched.
const renderSkeletonRows = (count) =>
  Array.from({ length: count }, (_, i) => (
    <li key={`skeleton-${i}`} className="cw-row cw-row--skeleton" aria-hidden="true">
      <span className="cw-skel cw-skel--avatar" />
      <span className="cw-row__main">
        <span className="cw-skel cw-skel--title" />
        <span className="cw-skel cw-skel--text" />
      </span>
      <span className="cw-row__meta">
        <span className="cw-skel cw-skel--meta" />
        <span className="cw-skel cw-skel--meta cw-skel--short" />
      </span>
      <span className="cw-skel cw-skel--btn" />
    </li>
  ));

// The API expects E.164 (+91XXXXXXXXXX); bare Indian numbers get the prefix.
const toDestination = (raw) => {
  const n = cleanNumber(raw);
  if (/^\d{10}$/.test(n)) return `+91${n}`;
  if (/^0\d{10}$/.test(n)) return `+91${n.slice(1)}`;
  if (/^91\d{10}$/.test(n)) return `+${n}`;
  return n;
};

const readStoredPos = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const clamp = (v, min, max) => Math.min(Math.max(v, min), Math.max(min, max));

const clampFab = ({ x, y }) => ({
  x: clamp(x, EDGE, window.innerWidth - FAB_SIZE - EDGE),
  y: clamp(y, EDGE, window.innerHeight - FAB_SIZE - EDGE),
});

const formatTimer = (s) =>
  `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

// "10:42 AM" today, "Yesterday", otherwise "28 Oct".
const formatWhen = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString([], { day: "numeric", month: "short" });
};

// "Today, 10:42 AM" / "Yesterday, 03:00 PM" / "28 Oct, 11:00 AM".
const formatDateTime = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const day = d.toDateString() === new Date().toDateString() ? "Today" : formatWhen(d);
  return `${day}, ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
};

const initials = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

/* Pointer-based dragging shared by the launcher and the panel header.
   Reports whether the gesture moved far enough to count as a drag, so the
   launcher can tell a tap from a drag. */
const useDrag = (onMove) => {
  const start = useRef(null);

  const onPointerDown = useCallback((e, origin) => {
    if (e.button !== 0) return;
    start.current = { px: e.clientX, py: e.clientY, ox: origin.x, oy: origin.y, moved: false };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, []);

  const onPointerMove = useCallback(
    (e) => {
      const s = start.current;
      if (!s) return;
      const dx = e.clientX - s.px;
      const dy = e.clientY - s.py;
      if (!s.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
      s.moved = true;
      onMove({ x: s.ox + dx, y: s.oy + dy });
    },
    [onMove]
  );

  const onPointerUp = useCallback((e) => {
    const moved = start.current?.moved ?? false;
    start.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    return moved;
  }, []);

  return { onPointerDown, onPointerMove, onPointerUp };
};

const CallWidget = () => {
  const { user, token } = useSelector((state) => state.auth);
  const { pathname } = useLocation();
  // Same audience as the dialer iframe widget: callers only.
  const hidden =
    !user?.idToken || user?.is_agency || user?.is_supervisor || pathname === "/sign-in";

  const [fabPos, setFabPos] = useState(
    () =>
      clampFab(
        readStoredPos() || {
          x: window.innerWidth - FAB_SIZE - 24,
          y: window.innerHeight - FAB_SIZE - 24,
        }
      )
  );
  const [open, setOpen] = useState(false);
  const [panelPos, setPanelPos] = useState({ x: 0, y: 0 });

  const [tab, setTab] = useState("keypad"); // keypad | recents | callbacks
  const [screen, setScreen] = useState("home"); // home | calling | incall | wrapup
  // Availability / phone menu that drops down from the header pill.
  const [statusMenu, setStatusMenu] = useState(false);
  const statusMenuRef = useRef(null);
  const statusPillRef = useRef(null);
  const [sheet, setSheet] = useState(null); // null | keypad | transfer | conference

  const [number, setNumber] = useState("");
  const [record, setRecord] = useState(true); // asked for on the next call
  const [recording, setRecording] = useState(false); // current call, per engine
  const [status, setStatus] = useState("offline"); // backend presence
  const [notConfigured, setNotConfigured] = useState(false); // no agent mapped
  const [diallable, setDiallable] = useState(null); // engine: registered and free
  const [engineCallId, setEngineCallId] = useState(null); // live call per presence
  const finishedCalls = useRef(new Set()); // call_ids already wrapped up
  const [contact, setContact] = useState(null);
  const [callId, setCallId] = useState(null);
  const [callerId, setCallerId] = useState(null);
  const [agentAnswered, setAgentAnswered] = useState(false);
  // In-flight request, so its button can't be pressed twice:
  // null | initiate | hangup | hold | transfer | participant | disposition | presence | phone
  const [pending, setPending] = useState(null);

  // Browser softphone. The instance (and the SIP credential inside it) lives
  // in a ref only; React state just mirrors what the UI needs.
  const phoneRef = useRef(null);
  const [phoneState, setPhoneState] = useState(PhoneState.OFFLINE);
  const [ringing, setRinging] = useState(false); // engine is ringing this browser
  const [muted, setMuted] = useState(false);
  const [poorLine, setPoorLine] = useState(false);
  const [dtmf, setDtmf] = useState("");

  const [held, setHeld] = useState(false);
  const [seconds, setSeconds] = useState(0);

  const [transferMode, setTransferMode] = useState("attended");
  const [transferTo, setTransferTo] = useState("");
  const [transferring, setTransferring] = useState(null); // attended transfer target

  // People who can be added to a call (GET /dialer/conference-participants/).
  const [targets, setTargets] = useState({ list: [], loading: false, error: null });
  const [targetSearch, setTargetSearch] = useState("");
  const [participantMode, setParticipantMode] = useState("listen");
  const [participants, setParticipants] = useState([]);

  const [disposition, setDisposition] = useState("");

  const [callbacks, setCallbacks] = useState({ list: [], loading: false, error: null });
  const [historyFilter, setHistoryFilter] = useState("");
  const [history, setHistory] = useState({ list: [], next: null, loading: false, error: null });
  const [stats, setStats] = useState(null);

  const numberRef = useRef(null);
  const [pressed, setPressed] = useState(null);
  const pressTimer = useRef(null);

  const panelSize = () => ({
    w: Math.min(PANEL_W, window.innerWidth - EDGE * 2),
    h: Math.min(PANEL_H, window.innerHeight - EDGE * 2),
  });

  const clampPanel = useCallback(({ x, y }) => {
    const { w, h } = panelSize();
    return {
      x: clamp(x, EDGE, window.innerWidth - w - EDGE),
      y: clamp(y, EDGE, window.innerHeight - h - EDGE),
    };
  }, []);

  // Keep things on screen when the window is resized.
  useEffect(() => {
    const onResize = () => {
      setFabPos((p) => clampFab(p));
      setPanelPos((p) => clampPanel(p));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [clampPanel]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(fabPos));
    } catch {
      /* position memory is a convenience only */
    }
  }, [fabPos]);

  const showError = (err, fallback) => message.error(err?.message || fallback);

  const applyPresence = useCallback((res) => {
    const d = res?.data;
    setNotConfigured(false);
    if (d?.presence) setStatus(d.presence);
    if (typeof d?.diallable === "boolean") setDiallable(d.diallable);
    // Only trust call_id while the agent is actually on or ringing a call:
    // presence can keep a dead call's id after the engine has dropped it.
    if (d && "call_id" in d) {
      setEngineCallId(LIVE_PRESENCE.includes(d.presence) ? d.call_id || null : null);
    }
  }, []);

  const presenceFailed = useCallback((err) => {
    setStatus("offline");
    // "No agent id on your profile": this user has no Call Engine agent.
    if (err?.httpStatus === 400 && /agent/i.test(err?.message || "")) setNotConfigured(true);
  }, []);

  const refreshPresence = useCallback(
    () => getAgentPresence(token).then(applyPresence).catch(presenceFailed),
    [token, applyPresence, presenceFailed]
  );

  // Presence is read when the widget loads, when the panel opens and after
  // each call, so the Call button reflects whether the agent can dial.
  const backHome = screen === "home";
  useEffect(() => {
    if (hidden || !backHome) return undefined;
    let cancelled = false;
    getAgentPresence(token)
      .then((res) => {
        if (!cancelled) applyPresence(res);
      })
      .catch((err) => {
        if (!cancelled) presenceFailed(err);
      });
    return () => {
      cancelled = true;
    };
  }, [hidden, token, open, backHome, applyPresence, presenceFailed]);

  useEffect(() => {
    if (hidden || status !== "wrap_up") return undefined;
    const t = setInterval(refreshPresence, WRAP_UP_POLL_MS);
    return () => clearInterval(t);
  }, [hidden, status, refreshPresence]);

  // Scheduled callbacks, refreshed each time the panel opens on the home screen.
  useEffect(() => {
    if (hidden || !open || !backHome) return undefined;
    let cancelled = false;
    setCallbacks((c) => ({ ...c, loading: true, error: null }));
    getCallbackList({ page: 1, page_size: 20 }, token)
      .then((res) => {
        if (cancelled) return;
        setCallbacks({
          list: res?.data?.results?.cases_with_feedback || [],
          loading: false,
          error: null,
        });
      })
      .catch((err) => {
        if (!cancelled) {
          setCallbacks({ list: [], loading: false, error: err?.message || "Could not load callbacks" });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [hidden, token, open, backHome]);

  // Call history: first page whenever Recents is shown (so it includes the
  // call just wrapped up) or the filter changes.
  const recentsTab = open && backHome && tab === "recents";
  useEffect(() => {
    if (hidden || !recentsTab) return undefined;
    let cancelled = false;
    // Start empty so the skeleton shows instead of the previous filter's rows.
    setHistory({ list: [], next: null, loading: true, error: null });
    const params = { page: 1, page_size: HISTORY_PAGE_SIZE };
    if (historyFilter) params.state = historyFilter;
    getCallHistory(params, token)
      .then((res) => {
        if (cancelled) return;
        const d = res?.data || {};
        setHistory({
          list: d.results || [],
          next: d.has_more ? d.next : null,
          loading: false,
          error: null,
        });
      })
      .catch((err) => {
        if (!cancelled) {
          setHistory({ list: [], next: null, loading: false, error: err?.message || "Could not load call history" });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [hidden, token, recentsTab, historyFilter]);

  const loadMoreHistory = async () => {
    if (!history.next || history.loading) return;
    setHistory((h) => ({ ...h, loading: true }));
    try {
      const d = (await getCallHistoryPage(history.next, token))?.data || {};
      setHistory((h) => ({
        list: [...h.list, ...(d.results || [])],
        next: d.has_more ? d.next : null,
        loading: false,
        error: null,
      }));
    } catch (err) {
      setHistory((h) => ({ ...h, loading: false }));
      showError(err, "Could not load more calls");
    }
  };

  // Conference: who can be added, fetched when the sheet opens and kept
  // fresh while it is open or anyone added is still on the call.
  const conferenceOpen = sheet === "conference";
  const hasParticipants = !!callId && participants.length > 0;
  useEffect(() => {
    if (hidden || (!conferenceOpen && !hasParticipants)) return undefined;
    let cancelled = false;
    const load = (first) => {
      if (first) setTargets((t) => ({ ...t, loading: true, error: null }));
      getConferenceTargets(token)
        .then((res) => {
          if (!cancelled) {
            setTargets({ list: Array.isArray(res?.data) ? res.data : [], loading: false, error: null });
          }
        })
        .catch((err) => {
          // A failed refresh keeps the list it already has.
          if (!cancelled && first) {
            setTargets({ list: [], loading: false, error: err?.message || "Could not load your team" });
          }
        });
    };
    load(true);
    const t = setInterval(() => load(false), TARGETS_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [hidden, token, conferenceOpen, hasParticipants]);

  // A participant who hung up their side reads as available again: they have
  // left, so stop showing them on the call and let them be added again.
  useEffect(() => {
    if (!targets.list.length) return;
    const free = new Set(targets.list.filter((t) => t.available).map((t) => t.user_id));
    setParticipants((list) => {
      const now = Date.now();
      const kept = list.filter((p) => !free.has(p.userId) || now - p.addedAt < PARTICIPANT_GRACE_MS);
      return kept.length === list.length ? list : kept;
    });
  }, [targets.list]);

  // Today's numbers, shown in the status menu.
  const statusMenuOpen = open && backHome && statusMenu;
  useEffect(() => {
    if (hidden || !statusMenuOpen) return undefined;
    let cancelled = false;
    getTelecallerStats(token)
      .then((res) => {
        if (!cancelled) setStats(res?.data?.telecaller_activity || null);
      })
      .catch(() => {
        if (!cancelled) setStats(null);
      });
    return () => {
      cancelled = true;
    };
  }, [hidden, token, statusMenuOpen]);

  // The menu belongs to the idle phone: a call, going live or closing the
  // panel puts it away, as does a click outside it or Escape.
  useEffect(() => {
    if (!open || !backHome) setStatusMenu(false);
  }, [open, backHome]);

  useEffect(() => {
    if (!statusMenu) return undefined;
    const onPointerDown = (e) => {
      if (statusMenuRef.current?.contains(e.target) || statusPillRef.current?.contains(e.target)) return;
      setStatusMenu(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setStatusMenu(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [statusMenu]);

  const goWrapup = () => {
    setSheet(null);
    setTransferring(null);
    setScreen("wrapup");
  };

  // Live call status. "calling" moves to "incall" once the customer answers,
  // and any ended state drops straight into the disposition wrap-up.
  const live = !!callId && (screen === "calling" || screen === "incall");
  useEffect(() => {
    if (!live) return undefined;
    let cancelled = false;
    let inFlight = false;

    const poll = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const res = await getCallStatus(callId, token);
        if (cancelled) return;
        const call = res?.data || {};
        // A call picked back up after a reload (or rung in by the engine)
        // only knows its call_id; the number comes from here, before any
        // early return, so even a call that already ended shows it.
        if (call.destination) {
          setContact((c) => (c && !c.number ? { ...c, number: call.destination } : c));
        }
        if (ENDED_STATES.includes(String(call.state).toLowerCase())) {
          setSheet(null);
          setTransferring(null);
          setScreen("wrapup");
          return;
        }
        setAgentAnswered(!!call.agent_answered);
        if (call.customer_answered || call.state === "bridged" || call.state === "held") {
          setScreen((s) => (s === "calling" ? "incall" : s));
        }
        // The engine owns hold; follow it (it can change from elsewhere).
        if (call.state === "held" || call.state === "bridged") setHeld(call.state === "held");
        if (call.recording_state) setRecording(call.recording_state === "recording");
        if (call.caller_id) setCallerId(call.caller_id);
        // talk_seconds is fractional (e.g. 18.03).
        if (typeof call.talk_seconds === "number") setSeconds(Math.floor(call.talk_seconds));
      } catch {
        /* a missed poll is retried on the next tick */
      } finally {
        inFlight = false;
      }
    };

    poll();
    const t = setInterval(poll, STATUS_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [live, callId, token]);

  // Ticks between polls so the timer moves every second.
  useEffect(() => {
    if (screen !== "incall" || held) return undefined;
    const t = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [screen, held]);

  /* ---------- softphone ---------- */

  const inCall = screen === "calling" || screen === "incall";
  const phoneOnline = [PhoneState.READY, PhoneState.RINGING, PhoneState.ON_CALL].includes(phoneState);
  // Going live: null, or { step: "mic" | "line" | "live", error } while the
  // widget connects the phone and makes the agent Available.
  const [goLive, setGoLive] = useState(null);
  const goingLive = useRef(false);
  // Settled by onPhoneState once SIP.js registers (or fails), so going live
  // can wait for the registration before touching presence.
  const registrationWaiter = useRef(null);

  const backToHome = () => {
    setContact(null);
    setCallId(null);
    setSheet(null);
    setTransferring(null);
    setScreen("home");
  };

  const onPhoneState = (next) => {
    setPhoneState(next);
    if (next === PhoneState.FAILED) {
      registrationWaiter.current?.reject(new Error("The phone system did not accept the connection."));
    } else if (next === PhoneState.READY) {
      if (registrationWaiter.current) registrationWaiter.current.resolve();
      // Re-registered after a drop, or a call just ended: confirm presence.
      else refreshPresence();
    }
  };

  const waitForRegistration = () => {
    const wait = new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        registrationWaiter.current = null;
        reject(new Error("The phone system is taking too long to respond."));
      }, REGISTER_TIMEOUT_MS);
      const settle = (fn) => (value) => {
        clearTimeout(timer);
        registrationWaiter.current = null;
        fn(value);
      };
      registrationWaiter.current = { resolve: settle(resolve), reject: settle(reject) };
    });
    wait.catch(() => {}); // observed later, or dropped if connecting fails first
    return wait;
  };

  /* The engine notices a new registration a few seconds after SIP.js reports
     it (measured ~3-5s), and only then accepts presence changes - a PATCH
     sent straight away comes back "offline" and does nothing. So wait until
     presence says registered, then make sure the agent is Available. */
  const confirmOnline = async () => {
    for (let attempt = 0; attempt < REGISTER_CONFIRM_TRIES; attempt += 1) {
      let res;
      try {
        res = await getAgentPresence(token);
      } catch (err) {
        presenceFailed(err);
        throw err;
      }
      applyPresence(res);
      const d = res?.data || {};
      if (d.registered !== false) {
        // wrap_up hands back to available by itself; calls are left alone.
        if (!READY_PRESENCE.includes(d.presence)) {
          applyPresence(await setAgentPresence("available", token));
        }
        return;
      }
      await new Promise((r) => setTimeout(r, REGISTER_CONFIRM_MS));
    }
    throw new Error("The phone system hasn't picked up your phone yet. Please try again.");
  };

  const onPhoneCall = (e) => {
    if (e.event === "ringing") {
      setRinging(true);
      // A ring this widget did not start: a teammate added this agent to
      // their call (conference). It has no call_id here, so it is joined as
      // a plain audio leg - no hold, transfer or disposition.
      if (screen === "home") {
        setContact({ name: "", number: "", joined: true });
        setCallId(null);
        setHeld(false);
        setRecording(false);
        setSeconds(0);
        setScreen("calling");
      }
    } else if (e.event === "connected") {
      setRinging(false);
      if (!callId && contact?.joined) {
        setSeconds(0);
        setScreen("incall");
        // Presence may name the teammate's call as this agent's own; never
        // pick it back up as a call to manage once this leg ends.
        getAgentPresence(token)
          .then((res) => {
            const id = res?.data?.call_id;
            if (id) finishedCalls.current.add(id);
          })
          .catch(() => {});
      }
    } else if (e.event === "muted") {
      setMuted(e.muted);
    } else if (e.event === "ended") {
      setRinging(false);
      setMuted(false);
      setPoorLine(false);
      setDtmf("");
      // The engine ended this agent's leg: hang-up, transfer or the customer
      // leaving. With a call_id there is a disposition to record.
      if (inCall) {
        if (callId) goWrapup();
        else backToHome();
      }
    }
  };

  const refreshIce = () =>
    getSoftphoneIceServers(token)
      .then((res) => {
        phoneRef.current?.setIceServers(res?.data?.ice_servers, res?.data?.ice_expires_at);
      })
      .catch(() => {
        message.warning("Could not refresh the phone's network relay. Retrying in a minute.");
        setTimeout(() => {
          if (phoneRef.current && phoneRef.current.state !== PhoneState.OFFLINE) {
            phoneHandlers.current.onIceExpiring();
          }
        }, 60000);
      });

  // The softphone outlives renders, so it calls back through this ref to
  // reach the current handlers rather than ones holding stale state.
  const phoneHandlers = useRef({});
  // The engine no longer sees this browser's registration (another tab took
  // the phone, or the registration lapsed) while SIP.js still believes it is
  // fine. Drop it so the widget stops claiming Ready; opening the phone again
  // goes live again.
  const onPhoneLost = async () => {
    await phoneRef.current?.disconnect();
    message.warning("Your phone was disconnected from the call server. Open the phone to reconnect.");
  };

  phoneHandlers.current = {
    onState: onPhoneState,
    onCall: onPhoneCall,
    onIceExpiring: refreshIce,
    onLost: onPhoneLost,
  };

  // While idle and Ready, check now and then that the engine still has this
  // phone registered. Two misses in a row, so the engine's few-second lag
  // after (re)registering is never mistaken for a lost phone.
  useEffect(() => {
    if (hidden || phoneState !== PhoneState.READY || !backHome || goLive) return undefined;
    let misses = 0;
    const t = setInterval(async () => {
      try {
        const res = await getAgentPresence(token);
        applyPresence(res);
        misses = res?.data?.registered === false ? misses + 1 : 0;
        if (misses >= 2) phoneHandlers.current.onLost();
      } catch {
        /* checked again on the next tick */
      }
    }, REGISTRATION_CHECK_MS);
    return () => clearInterval(t);
  }, [hidden, phoneState, backHome, goLive, token, applyPresence]);

  const getPhone = (SIP) => {
    if (!phoneRef.current) {
      const h = phoneHandlers;
      phoneRef.current = new Softphone(SIP, {
        autoAnswer: AUTO_ANSWER,
        onState: (s) => h.current.onState(s),
        onCall: (e) => h.current.onCall(e),
        onError: (msg) => message.warning(msg),
        onQuality: (q) => setPoorLine(q.poor),
        onIceExpiring: () => h.current.onIceExpiring(),
      });
    }
    return phoneRef.current;
  };

  /* One step from "offline" to "ready for calls": microphone, phone line
     (SIP credential + registration), then Available. Runs when the telecaller
     opens the widget, and from Connect Phone / Try again. Each run is a user
     click, so the SIP password is never rotated by a page load. */
  const startGoLive = async ({ takeOver = false } = {}) => {
    if (goingLive.current || notConfigured) return;
    goingLive.current = true;
    // Read the softphone itself: callers may run right after it was dropped,
    // before this render's phoneOnline caught up.
    const online = [PhoneState.READY, PhoneState.RINGING, PhoneState.ON_CALL].includes(
      phoneRef.current?.state
    );
    let step = online ? "live" : "mic";
    const toStep = (next) => {
      step = next;
      setGoLive({ step: next, error: null });
    };
    try {
      if (!online) {
        if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
          throw new Error("The phone needs the dashboard to be opened over HTTPS.");
        }
        toStep("mic");
        // A new session/ rotates the SIP password, which silently cuts off a
        // phone already registered in another tab or device - calls to it then
        // fail. So if the engine already sees one, ask before taking over.
        if (!takeOver) {
          const res = await getAgentPresence(token);
          applyPresence(res);
          if (res?.data?.registered === true) {
            const err = new Error(
              "Your phone is already connected in another tab or device. Use it there, or move the phone to this tab."
            );
            err.elsewhere = true;
            throw err;
          }
        }
        // Microphone first: session/ rotates the SIP password, so don't spend
        // one on a browser that can't use it. The probe is closed right away -
        // SIP.js opens its own stream for each call.
        try {
          const probe = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
          probe.getTracks().forEach((t) => t.stop());
        } catch {
          throw new Error("Please allow microphone access in your browser, then try again.");
        }
        toStep("line");
        const SIP = await loadSip();
        const res = await createSoftphoneSession(token);
        const registered = waitForRegistration();
        // Handed straight to the softphone; the credential is not kept here.
        await getPhone(SIP).connect(res?.data);
        await registered;
      }
      toStep("live");
      await confirmOnline();
      setGoLive(null);
      message.success("You're live and ready to call.");
    } catch (err) {
      registrationWaiter.current = null;
      if (err?.httpStatus === 400 && /agent/i.test(err?.message || "")) setNotConfigured(true);
      setGoLive({
        step,
        error: err?.message || "Something went wrong. Please try again.",
        elsewhere: !!err?.elsewhere,
      });
    } finally {
      goingLive.current = false;
    }
  };

  const disconnectPhone = async () => {
    if (pending || inCall) return;
    setPending("phone");
    try {
      // Flow doc: go on break first so no call is routed while going offline.
      await setAgentPresence("break", token).catch(() => {});
      await phoneRef.current?.disconnect();
    } finally {
      setPending(null);
      refreshPresence();
    }
  };

  // Logout, a role without the widget, or leaving the page: unregister and
  // drop the credential.
  useEffect(() => {
    if (hidden) phoneRef.current?.disconnect();
  }, [hidden]);

  useEffect(() => {
    const onUnload = () => {
      phoneRef.current?.disconnect();
    };
    window.addEventListener("beforeunload", onUnload);
    return () => {
      window.removeEventListener("beforeunload", onUnload);
      phoneRef.current?.disconnect();
    };
  }, []);

  const answerRing = () => {
    phoneRef.current?.answer();
  };

  const sendDigit = (d) => {
    if (phoneRef.current?.sendDigit(d)) setDtmf((v) => (v + d).slice(-18));
  };

  const fabDrag = useDrag(useCallback((p) => setFabPos(clampFab(p)), []));
  const panelDrag = useDrag(useCallback((p) => setPanelPos(clampPanel(p)), [clampPanel]));

  const openPanel = () => {
    const { w, h } = panelSize();
    // Open beside the launcher, flipping to whichever side has room.
    const x = fabPos.x + FAB_SIZE / 2 > window.innerWidth / 2 ? fabPos.x + FAB_SIZE - w : fabPos.x;
    const y = fabPos.y + FAB_SIZE / 2 > window.innerHeight / 2 ? fabPos.y - h - 12 : fabPos.y + FAB_SIZE + 12;
    setPanelPos(clampPanel({ x, y }));
    setOpen(true);
    // Opening the phone means "I want to call": if the agent isn't ready,
    // get them ready now instead of making them click Connect and Ready.
    if (screen === "home" && (!phoneOnline || !READY_PRESENCE.includes(status))) {
      startGoLive();
    }
  };

  const resetCallState = () => {
    setHeld(false);
    setSeconds(0);
    setSheet(null);
    setTransferTo("");
    setTransferring(null);
    setTargetSearch("");
    setParticipants([]);
    setAgentAnswered(false);
    setCallerId(null);
    setDtmf("");
    setPoorLine(false);
  };

  /* Right before dialling, ask the engine whether it can actually reach this
     agent. The widget's own Ready can be stale (the phone was taken over by
     another tab, or presence lags), and the engine accepts a call for an
     unregistered agent anyway - it then rings nothing, never times out, and
     holds the agent "on_call". Returns true when it is safe to dial. */
  const preflightCall = async () => {
    let d;
    try {
      const res = await getAgentPresence(token);
      applyPresence(res);
      d = res?.data || {};
    } catch {
      return true; // can't tell; let the initiate API decide
    }
    if (d.registered === false) {
      await phoneHandlers.current.onLost();
      // Nothing else holds the phone, so taking it over is safe.
      startGoLive({ takeOver: true });
      return false;
    }
    if (LIVE_PRESENCE.includes(d.presence)) {
      message.warning("You're still on another call. End it before starting a new one.");
      return false;
    }
    if (d.diallable === false) {
      message.info("Your phone isn't ready for a call yet. Try again in a few seconds.");
      return false;
    }
    return true;
  };

  const startCall = async (target) => {
    const c = target || { name: "", number: number || "" };
    if (!c.number || pending) return;
    setPending("initiate");
    const ok = await preflightCall();
    setPending(null);
    if (!ok) return;
    resetCallState();
    setRecording(record);
    setContact(c);
    setCallId(null);
    setScreen("calling");
    setPending("initiate");
    try {
      const res = await initiateCall(
        { destination: toDestination(c.number), record, require_agent_free: true },
        token
      );
      if (!res?.data?.call_id) throw new Error("No call ID returned");
      setCallId(res.data.call_id);
      setCallerId(res.data.caller_id || null);
    } catch (err) {
      showError(err, "Could not place the call");
      setContact(null);
      setScreen("home");
    } finally {
      setPending(null);
    }
  };

  // Hang-up goes through Django so the business call flow stays central; the
  // engine then sends the browser its BYE.
  const endCall = async () => {
    if (pending === "initiate" || pending === "hangup") return;
    if (!callId) {
      // A ring this widget did not start has no call_id for Django.
      phoneRef.current?.hangup();
      return;
    }
    setPending("hangup");
    try {
      await hangupCall(callId, token);
    } catch (err) {
      if (err?.httpStatus === 404) {
        // The engine no longer has this call (it died without telling
        // Django), so there is nothing to hang up or record. Drop it.
        setPending(null);
        finishedCalls.current.add(callId);
        phoneRef.current?.hangup();
        message.info("This call had already ended.");
        backToHome();
        return;
      }
      // 409: the call state no longer allows hang-up, i.e. it already ended.
      if (err?.httpStatus !== 409) {
        showError(err, "Could not end the call");
        setPending(null);
        return;
      }
    }
    setPending(null);
    goWrapup();
  };

  // Declining the engine's ring ends the whole call, so it is a hang-up.
  const rejectRing = () => {
    if (callId) endCall();
    else phoneRef.current?.reject();
  };

  const toggleHold = async () => {
    if (!callId || pending) return;
    setPending("hold");
    try {
      await (held ? resumeCall(callId, token) : holdCall(callId, token));
      setHeld((v) => !v);
    } catch (err) {
      showError(err, held ? "Could not resume the call" : "Could not put the call on hold");
    } finally {
      setPending(null);
    }
  };

  const startTransfer = async () => {
    if (!callId || !transferTo || pending) return;
    const to = toDestination(transferTo);
    setPending("transfer");
    try {
      await transferCall(callId, { mode: transferMode, to }, token);
      if (transferMode === "blind") {
        // The customer goes straight to the target and this agent exits.
        goWrapup();
      } else {
        setTransferring(to);
        setSheet(null);
      }
    } catch (err) {
      showError(err, "Could not transfer the call");
    } finally {
      setPending(null);
    }
  };

  const finishTransfer = async (complete) => {
    if (!callId || pending) return;
    setPending("transfer");
    try {
      if (complete) {
        await completeTransfer(callId, token);
        goWrapup();
      } else {
        await cancelTransfer(callId, token);
        setTransferring(null);
      }
    } catch (err) {
      showError(err, complete ? "Could not complete the transfer" : "Could not cancel the transfer");
    } finally {
      setPending(null);
    }
  };

  const addToCall = async (target) => {
    if (!callId || !target?.user_id || pending) return;
    setPending("participant");
    try {
      const res = await addParticipant(
        callId,
        { user_id: target.user_id, mode: participantMode },
        token
      );
      setParticipants((list) => [
        ...list,
        {
          id: res?.data?.participant_id,
          userId: target.user_id,
          name: target.name,
          role: target.role,
          mode: res?.data?.mode || participantMode,
          muted: false,
          addedAt: Date.now(),
        },
      ]);
    } catch (err) {
      showError(err, "Could not add the participant");
    } finally {
      setPending(null);
    }
  };

  const changeParticipant = async (p, patch) => {
    if (!callId || !p.id || pending) return;
    const payload = patch.mode === "whisper" ? { ...patch, whisper_target: "agent" } : patch;
    setPending("participant");
    try {
      await updateParticipant(callId, p.id, payload, token);
      setParticipants((list) => list.map((x) => (x.id === p.id ? { ...x, ...patch } : x)));
    } catch (err) {
      showError(err, "Could not update the participant");
    } finally {
      setPending(null);
    }
  };

  const dropParticipant = async (p) => {
    if (!callId || !p.id || pending) return;
    setPending("participant");
    try {
      await removeParticipant(callId, p.id, token);
      setParticipants((list) => list.filter((x) => x.id !== p.id));
    } catch (err) {
      showError(err, "Could not remove the participant");
    } finally {
      setPending(null);
    }
  };

  // Resolves true once the agent is in the asked-for status.
  const changeStatus = async (key) => {
    if (key === status) return true;
    if (pending) return false;
    setPending("presence");
    try {
      const res = await setAgentPresence(key, token);
      setStatus(res?.data?.presence || key);
      return true;
    } catch (err) {
      showError(err, "Could not update your status");
      return false;
    } finally {
      setPending(null);
    }
  };

  const finishWrapup = () => {
    // Presence can report a finished call for a moment; don't bring it back.
    if (callId) finishedCalls.current.add(callId);
    setDisposition("");
    setContact(null);
    setCallId(null);
    setCallerId(null);
    setParticipants([]);
    setNumber("");
    setScreen("home");
  };

  const saveDisposition = async () => {
    if (!disposition || pending) return;
    if (!callId) {
      finishWrapup();
      return;
    }
    setPending("disposition");
    try {
      await saveCallDisposition(callId, disposition, token);
      message.success("Disposition saved");
      finishWrapup();
    } catch (err) {
      showError(err, "Could not save the disposition");
    } finally {
      setPending(null);
    }
  };

  const currentStatus =
    AGENT_STATUSES.find((s) => s.key === status) ||
    AGENT_STATUSES.find((s) => s.key === "offline");
  // Until the browser phone is registered, the header shows the phone's
  // state rather than the work availability. During a call presence isn't
  // re-read, so the widget's own state decides.
  const pill = inCall
    ? ON_CALL_STATUS
    : goLive && !goLive.error
      ? GOING_LIVE_LABEL
      : phoneOnline
        ? currentStatus
        : PHONE_LABELS[phoneState] || PHONE_LABELS.offline;
  const canDial =
    phoneState === PhoneState.READY && status === "available" && diallable !== false && !pending;
  // Phone not usable yet: say so above the keypad, where it can't be missed.
  const phoneBlocked = notConfigured || !phoneOnline;

  /* Presence carries the agent's live call_id. If one shows up while this
     widget is idle (the page was reloaded mid-call), pick the call back up so
     it can still be held, transferred, hung up and given a disposition. */
  useEffect(() => {
    if (!engineCallId || screen !== "home" || finishedCalls.current.has(engineCallId)) return;
    setHeld(false);
    setSeconds(0);
    setRecording(false);
    setContact({ name: "", number: "", recovered: true });
    setCallId(engineCallId);
    setScreen("calling");
  }, [engineCallId, screen]);
  const dialing = open && screen === "home" && tab === "keypad";
  const dueCallbacks = callbacks.list.filter(
    (c) => c.callback_date_time && new Date(c.callback_date_time) <= new Date()
  ).length;

  // Light up the on-screen key that matches a physical key press.
  const flashKey = useCallback((k) => {
    setPressed(k);
    clearTimeout(pressTimer.current);
    pressTimer.current = setTimeout(() => setPressed(null), 140);
  }, []);

  useEffect(() => () => clearTimeout(pressTimer.current), []);

  const appendNumber = useCallback(
    (d) => setNumber((n) => cleanNumber(n + d).slice(0, MAX_NUMBER)),
    []
  );

  // Focus the number field whenever the keypad is showing, so typing just works.
  useEffect(() => {
    if (dialing) numberRef.current?.focus({ preventScroll: true });
  }, [dialing]);

  /* System keyboard support. Keys typed while the keypad is showing go to it,
     as long as focus isn't in some other field - the number input itself
     handles its own typing natively. */
  useEffect(() => {
    if (!dialing) return undefined;

    const isOtherField = (el) =>
      el &&
      el !== numberRef.current &&
      (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable);

    const onKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey || isOtherField(document.activeElement)) return;
      const inNumberField = document.activeElement === numberRef.current;

      if (DIAL_CHAR.test(e.key)) {
        flashKey(e.key === "+" ? "0" : e.key);
        if (inNumberField) return; // native input inserts it
        e.preventDefault();
        appendNumber(e.key);
      } else if (e.key === "Backspace" && !inNumberField) {
        e.preventDefault();
        setNumber((n) => n.slice(0, -1));
      } else if (e.key === "Enter") {
        e.preventDefault();
        document.getElementById("cw-call-btn")?.click();
      }
    };

    const onPaste = (e) => {
      if (isOtherField(document.activeElement) || document.activeElement === numberRef.current) return;
      const text = e.clipboardData?.getData("text") || "";
      if (!text) return;
      e.preventDefault();
      appendNumber(text);
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("paste", onPaste);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("paste", onPaste);
    };
  }, [dialing, flashKey, appendNumber]);

  /* ---------- pieces ---------- */

  const renderKeypad = (onKey) => (
    <div className="cw-keypad">
      {KEYS.map(([d, l]) => (
        <button
          key={d}
          type="button"
          tabIndex={-1}
          className={`cw-key ${pressed === d ? "is-pressed" : ""}`}
          onMouseDown={(e) => e.preventDefault()} // keep focus in the number field
          onClick={() => onKey(d)}
        >
          <span className="cw-key__digit">{d}</span>
          <span className="cw-key__letters">{l}</span>
        </button>
      ))}
    </div>
  );

  const renderHomeKeypad = () => (
    <div className="cw-dialer">
      {phoneBlocked && renderPhoneNotice()}
      <div className="cw-modes" role="radiogroup" aria-label="Call recording">
        <button
          type="button"
          className={`cw-mode ${record ? "is-active" : ""}`}
          onClick={() => setRecord(true)}
        >
          Record call
        </button>
        <button
          type="button"
          className={`cw-mode ${!record ? "is-active" : ""}`}
          onClick={() => setRecord(false)}
        >
          Don&apos;t record
        </button>
      </div>

      <div className={`cw-number ${number.length > 13 ? "is-long" : ""}`}>
        <input
          ref={numberRef}
          value={number}
          onChange={(e) => setNumber(cleanNumber(e.target.value).slice(0, MAX_NUMBER))}
          placeholder="Enter number"
          inputMode="tel"
          autoComplete="off"
          aria-label="Phone number"
        />
        {number && (
          <button
            type="button"
            className="cw-backspace"
            aria-label="Delete digit"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setNumber((n) => n.slice(0, -1))}
          >
            ⌫
          </button>
        )}
      </div>

      <div className="cw-callerid">
        <kbd>0-9</kbd> type or paste · <kbd>Enter</kbd> to call
      </div>

      {renderKeypad(appendNumber)}

      <div className="cw-dial-actions">
        <button
          type="button"
          id="cw-call-btn"
          className="cw-round cw-round--call"
          aria-label="Call"
          disabled={!number || !canDial}
          onClick={() => startCall()}
        >
          <PhoneFilled />
        </button>
      </div>
      {!phoneBlocked && renderPhoneNotice()}
    </div>
  );

  const connectButton = (className) => (
    <button
      type="button"
      className={className}
      disabled={!!pending || (goLive && !goLive.error) || phoneState === PhoneState.CONNECTING}
      onClick={() => startGoLive()}
    >
      <ApiOutlined />{" "}
      {(goLive && !goLive.error) || phoneState === PhoneState.CONNECTING
        ? "Connecting…"
        : "Connect Phone"}
    </button>
  );

  // Why the Call button is disabled, and what to do about it.
  const renderPhoneNotice = () => {
    if (notConfigured) return <p className="cw-hint">{NOT_CONFIGURED_HINT}</p>;
    if (!phoneOnline) {
      return (
        <div className="cw-connect">
          <p className="cw-hint">Connect your phone to start calling.</p>
          {connectButton("cw-primary")}
        </div>
      );
    }
    // Fix it right here rather than sending them off to another screen.
    if (NOT_READY_PRESENCE.includes(status)) {
      return (
        <div className="cw-connect">
          <p className="cw-hint">{STATUS_HINTS[status]}</p>
          <button
            type="button"
            className="cw-primary"
            disabled={!!pending}
            onClick={() => changeStatus("available")}
          >
            <CheckCircleFilled /> {pending === "presence" ? "Updating…" : "I'm ready"}
          </button>
        </div>
      );
    }
    if (STATUS_HINTS[status]) return <p className="cw-hint">{STATUS_HINTS[status]}</p>;
    if (diallable === false) {
      return <p className="cw-hint">Waiting for the phone system to confirm your phone…</p>;
    }
    return null;
  };

  const renderRecents = () => (
    <div className="cw-list-wrap">
      <div className="cw-seg">
        {HISTORY_FILTERS.map((f) => (
          <button
            key={f.key || "all"}
            type="button"
            className={historyFilter === f.key ? "is-active" : ""}
            onClick={() => setHistoryFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>
      <ul className="cw-list" aria-busy={history.loading}>
        {history.loading && !history.list.length && renderSkeletonRows(HISTORY_SKELETON_ROWS)}
        {history.list.map((c) => {
          const missed = !c.customer_answered;
          const talk = Math.floor(c.talk_seconds || 0);
          return (
            <li key={c.call_id} className="cw-row">
              <span className={`cw-avatar cw-avatar--${missed ? "missed" : "out"}`}>
                {missed ? <StopOutlined /> : <ArrowUpOutlined />}
              </span>
              <span className="cw-row__main">
                <b className={missed ? "is-missed" : ""}>{c.destination || "-"}</b>
                <small>
                  {[
                    CALL_STATE_LABELS[c.state] || c.state,
                    c.disposition ? dispositionLabel(c.disposition) : "No outcome",
                  ].join(" · ")}
                </small>
              </span>
              <span className="cw-row__meta">
                {/* started_at is Unix epoch seconds */}
                {c.started_at && <small>{formatWhen(c.started_at * 1000)}</small>}
                {talk > 0 && <small>{formatTimer(talk)}</small>}
              </span>
              <button
                type="button"
                className="cw-mini-call"
                aria-label={`Call ${c.destination}`}
                disabled={!canDial || !c.destination}
                onClick={() => startCall({ name: "", number: c.destination })}
              >
                <PhoneFilled />
              </button>
            </li>
          );
        })}
        {history.loading &&
          history.list.length > 0 &&
          renderSkeletonRows(HISTORY_MORE_SKELETON_ROWS)}
        {!history.loading && !history.list.length && (
          <li className="cw-empty">{history.error || "No calls yet"}</li>
        )}
      </ul>
      {/* Cursor pagination: there is no total and no going back, only "more". */}
      {history.next && history.list.length > 0 && (
        <div className="cw-load-more">
          <button
            type="button"
            className="cw-chip-btn"
            disabled={history.loading}
            onClick={loadMoreHistory}
          >
            {history.loading ? "Loading…" : "Load more"}
          </button>
        </div>
      )}
    </div>
  );

  const renderCallbacks = () => (
    <div className="cw-list-wrap">
      <div className="cw-section-title">Scheduled callbacks</div>
      <ul className="cw-list">
        {callbacks.list.map((c, i) => {
          const due = c.callback_date_time && new Date(c.callback_date_time) <= new Date();
          return (
            <li key={c.id ?? `${c.loan_no}-${i}`} className="cw-row">
              <span className="cw-avatar">{initials(c.customer_name) || <UserOutlined />}</span>
              <span className="cw-row__main">
                <b>{c.customer_name || "-"}</b>
                <small>{[c.loan_no, c.phone_number].filter(Boolean).join(" · ")}</small>
                {c.callback_date_time && (
                  <small className={`cw-due ${due ? "is-due" : ""}`}>
                    <ClockCircleOutlined /> {formatDateTime(c.callback_date_time)}
                  </small>
                )}
              </span>
              <button
                type="button"
                className="cw-mini-call"
                aria-label={`Call ${c.customer_name || c.phone_number}`}
                disabled={!canDial || !c.phone_number}
                onClick={() =>
                  startCall({ name: c.customer_name, number: c.phone_number, loan: c.loan_no })
                }
              >
                <PhoneFilled />
              </button>
            </li>
          );
        })}
        {!callbacks.list.length && (
          <li className="cw-empty">
            {callbacks.loading ? "Loading callbacks…" : callbacks.error || "No callbacks scheduled"}
          </li>
        )}
      </ul>
    </div>
  );

  const home = screen === "home";

  // Going live: what's happening, step by step, in plain words - and what to
  // do if it fails.
  const renderGoLive = () => {
    const current = GO_LIVE_STEPS.findIndex((s) => s.key === goLive.step);
    const failed = !!goLive.error;
    return (
      <div className="cw-golive" role="status" aria-live="polite">
        <span className={`cw-golive__icon ${failed ? "is-error" : ""}`}>
          {failed ? <WarningOutlined /> : <PhoneFilled />}
        </span>
        <h3>
          {goLive.elsewhere
            ? "Phone open somewhere else"
            : failed
              ? "Couldn't get you live"
              : "Getting you ready to call"}
        </h3>
        <p>{failed ? goLive.error : "This only takes a few seconds."}</p>

        <ol className="cw-golive__steps">
          {GO_LIVE_STEPS.map((s, i) => {
            let state = "todo";
            if (i < current) state = "done";
            else if (i === current) state = failed ? "error" : "active";
            return (
              <li key={s.key} className={`is-${state}`}>
                <span className="cw-golive__mark">
                  {state === "done" && <CheckCircleFilled />}
                  {state === "active" && <LoadingOutlined />}
                  {state === "error" && <CloseCircleFilled />}
                  {state === "todo" && <i />}
                </span>
                <span className="cw-golive__text">
                  <b>{s.label}</b>
                  {state === "active" && <small>{s.hint}</small>}
                </span>
              </li>
            );
          })}
        </ol>

        {failed && (
          <div className="cw-golive__actions">
            <button type="button" className="cw-secondary" onClick={() => setGoLive(null)}>
              Not now
            </button>
            {goLive.elsewhere ? (
              <button
                type="button"
                className="cw-primary"
                onClick={() => startGoLive({ takeOver: true })}
              >
                Use this tab
              </button>
            ) : (
              !notConfigured && (
                <button type="button" className="cw-primary" onClick={() => startGoLive()}>
                  Try again
                </button>
              )
            )}
          </div>
        )}
      </div>
    );
  };

  const statValue = (v) => (v === undefined || v === null ? "–" : v);

  /* Header pill menu. Only Ready and On Break are the agent's to pick; the
     engine's own states (ringing, on call, wrap-up) just show in the pill. */
  const renderStatusMenu = () => {
    const engineOwned = phoneOnline && !AGENT_STATUSES.find((s) => s.key === status)?.settable;
    return (
      <div className="cw-status-menu" ref={statusMenuRef} role="dialog" aria-label="Your status">
        {phoneOnline ? (
          <>
            <div className="cw-avail" role="radiogroup" aria-label="Availability">
              {SETTABLE_STATUSES.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  role="radio"
                  aria-checked={status === s.key}
                  className={`cw-avail__opt cw-tone--${s.tone} ${status === s.key ? "is-active" : ""}`}
                  disabled={!!pending}
                  onClick={async () => {
                    if (await changeStatus(s.key)) setStatusMenu(false);
                  }}
                >
                  {s.icon}
                  {s.label}
                </button>
              ))}
            </div>
            {engineOwned && STATUS_HINTS[status] && (
              <p className="cw-status-menu__note">{STATUS_HINTS[status]}</p>
            )}
          </>
        ) : (
          <p className="cw-status-menu__note">
            {notConfigured ? NOT_CONFIGURED_HINT : "Your phone is offline. Connect it to take calls."}
          </p>
        )}

        <div className="cw-stats">
          <div><b>{statValue(stats?.todays_call)}</b><small>Calls today</small></div>
          <div><b>{statValue(stats?.this_month_ptp)}</b><small>PTPs this month</small></div>
          <div><b>{statValue(stats?.total_ptp)}</b><small>Total PTPs</small></div>
        </div>

        {!notConfigured && (
          <div className="cw-status-menu__phone">
            <span className={`cw-phone__state cw-tone--${phoneOnline ? "green" : "muted"}`}>
              <i />
              {phoneOnline ? "Phone connected" : "Phone offline"}
            </span>
            {phoneOnline ? (
              <button
                type="button"
                className="cw-text-btn"
                disabled={!!pending}
                onClick={() => {
                  setStatusMenu(false);
                  disconnectPhone();
                }}
              >
                <DisconnectOutlined /> {pending === "phone" ? "Disconnecting…" : "Disconnect"}
              </button>
            ) : (
              <button
                type="button"
                className="cw-text-btn"
                disabled={!!pending || phoneState === PhoneState.CONNECTING}
                onClick={() => {
                  setStatusMenu(false);
                  startGoLive();
                }}
              >
                <ApiOutlined /> Connect
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  // Engine-side controls need the customer on the line; mute and DTMF only
  // need this browser's leg to be up.
  const controlsLocked = screen === "calling" || !!pending || !!transferring;
  const agentLegUp = phoneState === PhoneState.ON_CALL;
  // On a teammate's call this agent only has their own audio to control.
  const joined = !!contact?.joined && !callId;
  const ALL_CONTROLS = [
    { key: "mute", label: muted ? "Unmute" : "Mute", icon: muted ? <AudioMutedOutlined /> : <AudioOutlined />, active: muted, disabled: !agentLegUp, onClick: () => phoneRef.current?.toggleMute() },
    { key: "hold", label: held ? "Resume" : "Hold", icon: held ? <CaretRightOutlined /> : <PauseOutlined />, active: held, disabled: controlsLocked, onClick: toggleHold },
    { key: "keypad", label: "Keypad", icon: <AppstoreOutlined />, active: sheet === "keypad", disabled: !agentLegUp, onClick: () => setSheet("keypad") },
    { key: "transfer", label: "Transfer", icon: <SwapOutlined />, active: sheet === "transfer", disabled: controlsLocked, onClick: () => setSheet("transfer") },
    { key: "conference", label: "Conference", icon: <UsergroupAddOutlined />, active: sheet === "conference", disabled: controlsLocked, onClick: () => setSheet("conference") },
  ];
  const CONTROLS = joined ? ALL_CONTROLS.filter((c) => c.key === "mute") : ALL_CONTROLS;

  const renderTransferSheet = () => (
    <div className="cw-form">
      <div className="cw-seg">
        {TRANSFER_MODES.map((m) => (
          <button
            key={m.key}
            type="button"
            className={transferMode === m.key ? "is-active" : ""}
            onClick={() => setTransferMode(m.key)}
          >
            {m.label}
          </button>
        ))}
      </div>
      <div className="cw-number cw-number--sm">
        <input
          value={transferTo}
          onChange={(e) => setTransferTo(cleanNumber(e.target.value).slice(0, MAX_NUMBER))}
          placeholder="Phone number"
          inputMode="tel"
          aria-label="Transfer to"
        />
      </div>
      <p className="cw-form__note">{TRANSFER_MODES.find((m) => m.key === transferMode)?.hint}</p>
      <button
        type="button"
        className="cw-primary"
        disabled={!transferTo || !!pending}
        onClick={startTransfer}
      >
        {pending === "transfer" ? "Transferring…" : "Transfer"}
      </button>
    </div>
  );

  const renderConferenceTargets = () => {
    const q = targetSearch.trim().toLowerCase();
    const onCall = new Set(participants.map((p) => p.userId));
    const list = targets.list.filter(
      (t) => !q || `${t.name} ${t.role}`.toLowerCase().includes(q)
    );
    return (
      <>
        <div className="cw-section-title">Add someone</div>
        <div className="cw-seg" role="radiogroup" aria-label="Join as">
          {PARTICIPANT_MODES.map((m) => (
            <button
              key={m.key}
              type="button"
              className={participantMode === m.key ? "is-active" : ""}
              onClick={() => setParticipantMode(m.key)}
            >
              {m.label}
            </button>
          ))}
        </div>
        {targets.list.length > 5 && (
          <label className="cw-search">
            <SearchOutlined />
            <input
              value={targetSearch}
              onChange={(e) => setTargetSearch(e.target.value)}
              placeholder="Search name or role"
            />
          </label>
        )}
        <ul className="cw-list cw-participants" aria-busy={targets.loading}>
          {targets.loading && !targets.list.length && renderSkeletonRows(3)}
          {list.map((t) => {
            const added = onCall.has(t.user_id);
            return (
              <li key={t.user_id} className="cw-row">
                <span className="cw-avatar">
                  {initials(t.name) || <UserOutlined />}
                  <i className={`cw-presence ${t.available ? "is-on" : ""}`} />
                </span>
                <span className="cw-row__main">
                  <b>{t.name}</b>
                  <small>
                    {t.role} · {t.available ? "Available" : "Busy"}
                  </small>
                </span>
                <button
                  type="button"
                  className="cw-chip-btn"
                  // The engine dials their phone, so they must be free to answer.
                  disabled={!t.available || added || !!pending}
                  onClick={() => addToCall(t)}
                >
                  {added ? "Added" : "Add"}
                </button>
              </li>
            );
          })}
          {!targets.loading && !list.length && (
            <li className="cw-empty">
              {targets.error || (q ? "No one matches your search" : "No one to add right now")}
            </li>
          )}
        </ul>
      </>
    );
  };

  const renderConferenceSheet = () => (
    <div className="cw-form">
      {participants.length > 0 && <div className="cw-section-title">On this call</div>}
      <ul className="cw-list cw-participants">
        {participants.map((p) => (
          <li key={p.id} className="cw-participant">
            <div className="cw-row">
              <span className="cw-avatar">{initials(p.name) || <UserOutlined />}</span>
              <span className="cw-row__main">
                <b>{p.name || `User #${p.userId}`}</b>
                <small>
                  {[p.role, p.muted ? "Muted" : "Can be heard"].filter(Boolean).join(" · ")}
                </small>
              </span>
              <button
                type="button"
                className="cw-icon-btn"
                aria-label={p.muted ? "Unmute" : "Mute"}
                disabled={!!pending}
                onClick={() => changeParticipant(p, { muted: !p.muted })}
              >
                {p.muted ? <AudioMutedOutlined /> : <AudioOutlined />}
              </button>
              <button
                type="button"
                className="cw-icon-btn"
                aria-label="Remove from call"
                disabled={!!pending}
                onClick={() => dropParticipant(p)}
              >
                <DeleteOutlined />
              </button>
            </div>
            <div className="cw-seg">
              {PARTICIPANT_MODES.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  className={p.mode === m.key ? "is-active" : ""}
                  disabled={!!pending}
                  onClick={() => p.mode !== m.key && changeParticipant(p, { mode: m.key })}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </li>
        ))}
      </ul>

      {renderConferenceTargets()}
    </div>
  );

  const renderSheet = () => {
    if (!sheet) return null;
    const titles = {
      keypad: "Keypad",
      transfer: "Transfer call",
      conference: "Add to call",
    };
    return (
      <div className="cw-sheet">
        <div className="cw-sheet__head">
          <button type="button" className="cw-icon-btn" aria-label="Back" onClick={() => setSheet(null)}>
            <ArrowLeftOutlined />
          </button>
          <b>{titles[sheet]}</b>
        </div>
        {sheet === "keypad" && (
          <>
            <div className="cw-dtmf">{dtmf || " "}</div>
            {renderKeypad(sendDigit)}
          </>
        )}
        {sheet === "transfer" && renderTransferSheet()}
        {sheet === "conference" && renderConferenceSheet()}
      </div>
    );
  };

  const renderInCall = () => (
    <div className="cw-call">
      <div className={`cw-call__who ${screen === "calling" ? "is-ringing" : ""}`}>
        <span className="cw-avatar-ring">
          <i className="cw-ripple" />
          <i className="cw-ripple" />
          <span className="cw-avatar cw-avatar--xl">
            {initials(contact?.name) || <UserOutlined />}
          </span>
        </span>
        <h3>
          {contact?.name ||
            contact?.number ||
            (joined ? "Conference call" : contact?.recovered ? "Current call" : "Incoming call")}
        </h3>
        {contact?.name && <p>{contact?.number}</p>}
        {joined && <p>A teammate added you to their call</p>}
        {callerId && <p>Caller ID {callerId}</p>}
        {contact?.loan && <span className="cw-tag">{contact.loan}</span>}
        <div className={`cw-call__state ${held ? "is-held" : ""}`}>
          {screen === "calling" ? (
            <span className="cw-ringing">
              {ringing && !AUTO_ANSWER
                ? "Your phone is ringing"
                : joined
                  ? "Joining the call"
                  : agentLegUp || agentAnswered
                  ? "Calling customer"
                  : "Connecting your phone"}
              <i>.</i><i>.</i><i>.</i>
            </span>
          ) : held ? (
            <>On hold · {formatTimer(seconds)}</>
          ) : (
            <>{formatTimer(seconds)}</>
          )}
        </div>
        <div className="cw-call__flags">
          {recording && screen === "incall" && <span className="cw-flag cw-flag--rec">● REC</span>}
          {muted && <span className="cw-flag">Muted</span>}
          {poorLine && (
            <span className="cw-flag cw-flag--warn">
              <WarningOutlined /> Poor line
            </span>
          )}
          {participants.length > 0 && (
            <span className="cw-flag">+{participants.length} on call</span>
          )}
        </div>
      </div>

      {transferring && (
        <div className="cw-transfer-bar">
          <small>Transferring to <b>{transferring}</b></small>
          <div className="cw-transfer-bar__actions">
            <button
              type="button"
              className="cw-secondary"
              disabled={!!pending}
              onClick={() => finishTransfer(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="cw-primary"
              disabled={!!pending}
              onClick={() => finishTransfer(true)}
            >
              Complete
            </button>
          </div>
        </div>
      )}

      {/* Four controls fill one row; five wrap as 3 + 2; a lone one centres. */}
      <div
        className={`cw-controls ${CONTROLS.length > 4 ? "cw-controls--3" : ""} ${
          CONTROLS.length === 1 ? "cw-controls--1" : ""
        }`}
      >
        {CONTROLS.map((c) => (
          <button
            key={c.key}
            type="button"
            className={`cw-control ${c.active ? "is-active" : ""} cw-control--${c.key}`}
            disabled={c.disabled}
            onClick={c.onClick}
          >
            <span className="cw-control__icon">{c.icon}</span>
            <small>{c.label}</small>
          </button>
        ))}
      </div>

      <div className="cw-dial-actions">
        {ringing && !AUTO_ANSWER ? (
          <>
            <button
              type="button"
              className="cw-round cw-round--end"
              aria-label="Reject"
              disabled={pending === "hangup"}
              onClick={rejectRing}
            >
              <PhoneFilled />
            </button>
            <button
              type="button"
              className="cw-round cw-round--call"
              aria-label="Answer"
              onClick={answerRing}
            >
              <PhoneFilled />
            </button>
          </>
        ) : (
          <button
            type="button"
            className="cw-round cw-round--end"
            aria-label="End call"
            disabled={pending === "initiate" || pending === "hangup"}
            onClick={endCall}
          >
            <PhoneFilled />
          </button>
        )}
      </div>

      {renderSheet()}
    </div>
  );

  const renderWrapup = () => (
    <div className="cw-wrapup">
      <div className="cw-wrapup__summary">
        <span className="cw-avatar">{initials(contact?.name) || <UserOutlined />}</span>
        <div>
          <b>{contact?.name || contact?.number}</b>
          <small>
            {contact?.name && `${contact.number} · `}Duration {formatTimer(seconds)}
          </small>
        </div>
      </div>

      <div className="cw-section-title">Disposition</div>
      <div className="cw-dispositions">
        {DISPOSITIONS.map((d) => (
          <button
            key={d.value}
            type="button"
            className={`cw-chip ${disposition === d.value ? "is-active" : ""}`}
            onClick={() => setDisposition(d.value)}
          >
            {d.label}
          </button>
        ))}
      </div>

      <div className="cw-wrapup__actions">
        <button
          type="button"
          className="cw-secondary"
          disabled={!!pending}
          onClick={finishWrapup}
        >
          Skip
        </button>
        <button
          type="button"
          className="cw-primary"
          disabled={!disposition || !!pending}
          onClick={saveDisposition}
        >
          {pending === "disposition" ? "Saving…" : "Save & Next"}
        </button>
      </div>
    </div>
  );

  const TABS = [
    { key: "keypad", label: "Keypad", icon: <AppstoreOutlined /> },
    { key: "recents", label: "Recents", icon: <HistoryOutlined /> },
    { key: "callbacks", label: "Callbacks", icon: <ClockCircleOutlined />, badge: dueCallbacks },
  ];
  // The pill opens the status menu only on the idle phone.
  const pillMenu = home && !goLive;

  const { w: panelW, h: panelH } = panelSize();

  if (hidden) return null;

  return (
    <>
      {!open && (
        <button
          type="button"
          className={`cw-fab ${inCall ? "is-live" : ""}`}
          style={{ left: fabPos.x, top: fabPos.y }}
          aria-label="Open call panel"
          title="Drag to move, click to open"
          onPointerDown={(e) => fabDrag.onPointerDown(e, fabPos)}
          onPointerMove={fabDrag.onPointerMove}
          onPointerUp={(e) => {
            if (!fabDrag.onPointerUp(e)) openPanel();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openPanel();
            }
          }}
        >
          <PhoneFilled />
          <i className={`cw-fab__dot cw-tone--${pill.tone}`} />
          {inCall && <span className="cw-fab__timer">{formatTimer(seconds)}</span>}
        </button>
      )}

      {open && (
        <div
          className="cw-panel"
          role="dialog"
          aria-label="Calling"
          style={{ left: panelPos.x, top: panelPos.y, width: panelW, height: panelH }}
        >
          <div
            className="cw-header"
            onPointerDown={(e) => {
              if (e.target.closest("button")) return;
              panelDrag.onPointerDown(e, panelPos);
            }}
            onPointerMove={panelDrag.onPointerMove}
            onPointerUp={panelDrag.onPointerUp}
          >
            <HolderOutlined className="cw-header__grip" />
            <button
              type="button"
              ref={statusPillRef}
              className={`cw-status-pill cw-tone--${pill.tone} ${pillMenu ? "is-menu" : ""} ${
                pillMenu && statusMenu ? "is-open" : ""
              }`}
              aria-haspopup={pillMenu ? "dialog" : undefined}
              aria-expanded={pillMenu ? statusMenu : undefined}
              disabled={!pillMenu}
              onClick={() => setStatusMenu((v) => !v)}
            >
              <i />
              {pill.label}
              {pillMenu && <DownOutlined className="cw-status-pill__caret" />}
            </button>
            <span className="cw-header__spacer" />
            <button type="button" className="cw-icon-btn" aria-label="Minimize" onClick={() => setOpen(false)}>
              <MinusOutlined />
            </button>
            <button
              type="button"
              className="cw-icon-btn"
              aria-label="Close"
              onClick={() => {
                if (inCall) endCall();
                setOpen(false);
              }}
            >
              <CloseOutlined />
            </button>
          </div>

          {pillMenu && statusMenu && renderStatusMenu()}

          <div className="cw-body">
            {home && goLive && renderGoLive()}
            {home && !goLive && tab === "keypad" && renderHomeKeypad()}
            {home && !goLive && tab === "recents" && renderRecents()}
            {home && !goLive && tab === "callbacks" && renderCallbacks()}
            {inCall && renderInCall()}
            {screen === "wrapup" && renderWrapup()}
          </div>

          {home && !goLive && (
            <nav className="cw-tabs">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={`cw-tab ${tab === t.key ? "is-active" : ""}`}
                  onClick={() => setTab(t.key)}
                >
                  <span className="cw-tab__icon">
                    {t.icon}
                    {!!t.badge && <em>{t.badge}</em>}
                  </span>
                  <small>{t.label}</small>
                </button>
              ))}
            </nav>
          )}
        </div>
      )}
    </>
  );
};

export default CallWidget;
